import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar, ReferenceArea,
} from 'recharts';
import {
  Eye, Users, Globe, TrendingUp, Trophy, Send, Loader2, MapPin, Smartphone, Linkedin,
  Repeat, FileText, Mail, CreditCard, DollarSign, MousePointerClick, Newspaper, Mailbox, Save, CheckCircle2,
  Download, Megaphone, Plus, Trash2, UserX, Bell, AlertTriangle,
} from 'lucide-react';
import AdminHeader from './AdminHeader';
import {
  analyticsOverview, analyticsVisitors, reportSettings, saveReportSettings, sendReportNow,
  campaignsList, campaignCreate, campaignDelete, engagementSummary,
  engagementAlertSettings, saveEngagementAlertSettings, engagementNotifyNow, exportPageviews,
} from '../lib/marketingApi';

const DEVICE_COLORS = { desktop: '#0035A8', mobile: '#f97316', tablet: '#8b5cf6', unknown: '#94a3b8' };
const FUNNEL_COLORS = ['#0035A8', '#2563eb', '#7c3aed', '#0891b2', '#059669'];
const RANGES = [{ key: 7, label: '7d' }, { key: 30, label: '30d' }, { key: 90, label: '90d' }];
const CH_COLOR = { linkedin: '#0a66c2', facebook: '#1877f2', google: '#ea4335', email: '#f59e0b', twitter: '#1da1f2', instagram: '#e1306c', event: '#7c3aed', other: '#64748b' };
const CHANNELS = ['linkedin', 'facebook', 'google', 'email', 'twitter', 'instagram', 'event', 'other'];

function flag(code) {
  if (!code || code.length !== 2) return '🌐';
  try { return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt())); } catch (e) { return '🌐'; }
}

/* ---- primitives ---- */
function StatCard({ icon: Icon, label, value, sub, gradient }) {
  return (
    <div data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className={`absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20 blur-xl ${gradient}`} />
      <span className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow ${gradient}`}><Icon className="h-5 w-5" /></span>
      <div className="text-2xl font-extrabold tracking-tight text-slate-900">{value}</div>
      <div className="mt-0.5 text-[11px] font-medium text-slate-500">{label}</div>
      {sub && <div className="mt-1 text-[11px] font-semibold text-emerald-600">{sub}</div>}
    </div>
  );
}

function Panel({ title, icon: Icon, children, className = '', right }) {
  return (
    <div className={`rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
          {Icon && <Icon className="h-4 w-4 text-[#0035A8]" />} {title}
        </h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function BarList({ items, labelKey, valueKey, testid, accent = '#0035A8', prefixFlag }) {
  const max = Math.max(1, ...items.map((x) => x[valueKey] || 0));
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-3">
          {prefixFlag && <span className="text-base leading-none">{flag(it.code)}</span>}
          <span className="w-28 shrink-0 truncate text-xs font-medium text-slate-600" title={it[labelKey]}>{it[labelKey] || '—'}</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full transition-all" style={{ width: `${((it[valueKey] || 0) / max) * 100}%`, background: accent }} />
          </div>
          <span className="w-9 text-right text-xs font-bold text-slate-900">{it[valueKey]}</span>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
    </div>
  );
}

/* ---- infographic funnel ---- */
function Funnel({ stages }) {
  const top = Math.max(1, stages[0]?.count || 1);
  return (
    <div className="space-y-1.5" data-testid="analytics-funnel">
      {stages.map((s, i) => {
        const widthPct = 40 + (s.count / top) * 60; // never collapse to 0
        const prev = i > 0 ? stages[i - 1].count : null;
        const conv = prev ? Math.round((s.count / (prev || 1)) * 100) : null;
        return (
          <div key={s.stage}>
            {i > 0 && (
              <div className="flex items-center justify-center py-0.5">
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">▼ {conv}%</span>
              </div>
            )}
            <div className="mx-auto flex items-center justify-center rounded-lg py-3 text-white shadow-sm transition-all"
              style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, ${FUNNEL_COLORS[i]}, ${FUNNEL_COLORS[i]}cc)` }}>
              <div className="text-center">
                <div className="text-sm font-extrabold leading-none">{s.count.toLocaleString()}</div>
                <div className="mt-0.5 text-[10px] font-medium opacity-90">{s.stage}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Gauge({ value, label, color }) {
  const data = [{ name: label, value: Math.min(100, value), fill: color }];
  return (
    <div className="flex flex-col items-center">
      <div style={{ width: 110, height: 90 }} className="relative">
        <ResponsiveContainer>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={210} endAngle={-30}>
            <RadialBar background={{ fill: '#eef2f7' }} dataKey="value" cornerRadius={8} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pt-1">
          <span className="text-lg font-extrabold text-slate-900">{value}%</span>
        </div>
      </div>
      <div className="-mt-2 text-[11px] font-medium text-slate-500">{label}</div>
    </div>
  );
}

/* ---- weekly report settings ---- */
function ReportCard() {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    reportSettings().then((r) => {
      setCfg(r.settings); setEnabled(r.settings.enabled !== false);
      setRecipients((r.settings.recipients || []).join(', '));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const list = recipients.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
      const r = await saveReportSettings({ enabled, recipients: list, window_days: cfg?.window_days || 7 });
      setCfg(r.settings); setRecipients((r.settings.recipients || []).join(', '));
      toast.success('Weekly report settings saved');
    } catch (e) { toast.error('Could not save settings'); } finally { setSaving(false); }
  };
  const testSend = async () => {
    setSending(true);
    try { const r = await sendReportNow(); r.sent ? toast.success(`Test report sent to ${r.recipients.join(', ')}`) : toast.error(`Not sent: ${r.skipped || r.error}`); }
    catch (e) { toast.error('Could not send test'); } finally { setSending(false); }
  };

  return (
    <Panel title="Weekly snapshot email" icon={Mailbox} className="mt-4"
      right={<span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">Every Monday · 07:00 UTC</span>}>
      <p className="mb-3 text-xs text-slate-500">Emails this dashboard as a branded snapshot to the people below, automatically every Monday.</p>
      <label className="mb-1 block text-xs font-semibold text-slate-600">Recipients (comma-separated)</label>
      <textarea data-testid="report-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)}
        rows={2} placeholder="satish@billportal.io, growth@billportal.io"
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-[#0035A8] focus:outline-none" />
      <div className="mt-2 flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
          <input type="checkbox" data-testid="report-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-[#0035A8]" />
          Send automatically each week
        </label>
        <div className="flex items-center gap-2">
          <button data-testid="report-test-send" onClick={testSend} disabled={sending}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send me a test
          </button>
          <button data-testid="report-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#002B87] disabled:opacity-50">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </div>
    </Panel>
  );
}

/* ---- campaigns manager ---- */
function CampaignsCard({ campaigns, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', channel: 'linkedin', start_date: '', end_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.start_date) { toast.error('Add a name and start date'); return; }
    setSaving(true);
    try { await campaignCreate(form); toast.success('Campaign added'); setForm({ name: '', channel: 'linkedin', start_date: '', end_date: '', notes: '' }); setAdding(false); onChange(); }
    catch (e) { toast.error('Could not add campaign'); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm('Delete this campaign marker?')) return; try { await campaignDelete(id); toast.success('Deleted'); onChange(); } catch (e) { toast.error('Delete failed'); } };
  return (
    <Panel title="Campaigns" icon={Megaphone} className="mt-4"
      right={<button onClick={() => setAdding((v) => !v)} data-testid="campaign-add-toggle" className="inline-flex items-center gap-1 rounded-md bg-[#0035A8] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#002B87]"><Plus className="h-3.5 w-3.5" /> New campaign</button>}>
      <p className="mb-3 text-xs text-slate-500">Add a campaign window and it appears as a shaded band on the traffic chart above — so you can see the lift in views &amp; enquiries during (and after) the period.</p>
      {adding && (
        <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-5" data-testid="campaign-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" data-testid="campaign-name" className="col-span-2 h-9 rounded-md border border-slate-300 px-2 text-sm" />
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="campaign-channel" className="h-9 rounded-md border border-slate-300 px-2 text-sm capitalize">{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="campaign-start" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
          <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="campaign-end" className="h-9 rounded-md border border-slate-300 px-2 text-sm" />
          <button onClick={save} disabled={saving} data-testid="campaign-save" className="col-span-2 inline-flex h-9 items-center justify-center gap-1 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 sm:col-span-5">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save campaign'}</button>
        </div>
      )}
      <div className="space-y-2" data-testid="campaign-list">
        {campaigns.length === 0 && <p className="text-sm text-slate-400">No campaigns yet.</p>}
        {campaigns.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: CH_COLOR[c.channel] || '#64748b' }} />
            <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-slate-800">{c.name}</div><div className="text-[11px] capitalize text-slate-400">{c.channel} · {c.start_date}{c.end_date ? ` → ${c.end_date}` : ' → ongoing'}</div></div>
            <button onClick={() => del(c.id)} data-testid={`campaign-del-${c.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ---- needs attention (unengaged) ---- */
function NeedsAttentionCard({ data, onReload }) {
  const un = data.unengaged;
  const groups = [
    { key: 'enquiries', label: 'Website enquiries', rows: un.enquiries },
    { key: 'partners', label: 'Partner applications', rows: un.partners },
    { key: 'pre_registered', label: 'Pre-registered (trials)', rows: un.pre_registered },
    { key: 'post_registered', label: 'Post-registered (customers)', rows: un.post_registered },
  ];
  return (
    <Panel title="Needs attention · unengaged" icon={AlertTriangle} className="mt-4"
      right={<span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">{data.needs_attention} unassigned {'>'} {un.stale_days}d</span>}>
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] font-medium text-slate-500">Enquiries engaged</div><div className="mt-0.5 text-lg font-extrabold text-slate-900">{data.enquiries.engaged}<span className="text-sm font-medium text-slate-400"> / {data.enquiries.total}</span></div></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] font-medium text-slate-500">Registrations engaged</div><div className="mt-0.5 text-lg font-extrabold text-slate-900">{data.registrations.engaged}<span className="text-sm font-medium text-slate-400"> / {data.registrations.total}</span></div></div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="needs-attention-lists">
        {groups.map((g) => (
          <div key={g.key}>
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><UserX className="h-3.5 w-3.5 text-amber-500" /> {g.label} ({g.rows.length})</div>
            {g.rows.length === 0 ? <p className="text-xs text-emerald-600">All engaged 🎉</p> : (
              <ul className="space-y-1">
                {g.rows.slice(0, 6).map((r, i) => (<li key={i} className="truncate text-xs text-slate-600"><span className="font-semibold text-slate-800">{r.company || r.name || '—'}</span>{r.email ? ` · ${r.email}` : ''}</li>))}
                {g.rows.length > 6 && <li className="text-[11px] text-slate-400">…and {g.rows.length - 6} more</li>}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ---- engagement alert settings (Astrid) ---- */
function AlertSettingsCard() {
  const [recipients, setRecipients] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  useEffect(() => { engagementAlertSettings().then((r) => { setEnabled(r.settings.enabled !== false); setRecipients((r.settings.recipients || []).join(', ')); }).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const list = recipients.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean); const r = await saveEngagementAlertSettings({ enabled, recipients: list, window_days: 7 }); setRecipients((r.settings.recipients || []).join(', ')); toast.success('Alert settings saved'); }
    catch (e) { toast.error('Could not save'); } finally { setSaving(false); }
  };
  const notify = async () => { setNotifying(true); try { const r = await engagementNotifyNow(); r.sent ? toast.success(`Alert sent (${r.unengaged_total} unengaged)`) : toast.error(`Not sent: ${r.skipped || r.error}`); } catch (e) { toast.error('Could not send'); } finally { setNotifying(false); } };
  return (
    <Panel title="Unengaged alerts · from Astrid" icon={Bell} className="mt-4"
      right={<span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">Daily · 08:00 UTC</span>}>
      <p className="mb-3 text-xs text-slate-500">When a contact stays unassigned for 2+ days, Astrid emails these people a summary of who needs picking up (e.g. Hari, Domenic, Thomas).</p>
      <label className="mb-1 block text-xs font-semibold text-slate-600">Alert recipients (comma-separated)</label>
      <textarea data-testid="alert-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={2} placeholder="hari@billportal.io, domenic@billportal.io, thomas@billportal.io" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-[#0035A8] focus:outline-none" />
      <div className="mt-2 flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600"><input type="checkbox" data-testid="alert-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-[#0035A8]" /> Send the daily alert automatically</label>
        <div className="flex items-center gap-2">
          <button onClick={notify} disabled={notifying} data-testid="alert-notify-now" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{notifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send me a test</button>
          <button onClick={save} disabled={saving} data-testid="alert-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#002B87] disabled:opacity-50">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</button>
        </div>
      </div>
    </Panel>
  );
}

export default function MarketingAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadCampaigns = () => campaignsList().then((r) => setCampaigns(r.items || [])).catch(() => {});
  useEffect(() => {
    setLoading(true);
    Promise.all([analyticsOverview(days), analyticsVisitors(days, 50), campaignsList().catch(() => ({ items: [] })), engagementSummary().catch(() => null)])
      .then(([ov, vs, cs, eng]) => { setData(ov); setVisitors(vs.visitors || []); setCampaigns(cs.items || []); setEngagement(eng); })
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [days]);

  const doExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPageviews({ days });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `billportal_pageviews_${days}d.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (e) { toast.error('Export failed'); } finally { setExporting(false); }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AdminHeader active="dashboard" />
        <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </div>
    );
  }

  const t = data.totals;
  const conv = data.conversion;
  const reg = data.registrations;
  const rev = data.revenue || {};
  const payFunnel = data.paying_funnel || [];
  const nl = data.content.newsletter;
  const deviceData = data.devices.map((d) => ({ name: d.device, value: d.count }));
  const regData = [
    { name: 'Post-registered', value: reg.post_registered, fill: '#0035A8' },
    { name: 'Pre-registered', value: reg.pre_registered, fill: '#93c5fd' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketing-analytics">
      <AdminHeader active="dashboard" />

      {/* brand header band (black + coral accent) */}
      <div className="border-b-2 border-[#FF5757] bg-gradient-to-r from-[#0a0a0b] via-[#161618] to-[#0a0a0b]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-3 px-5 py-6 sm:px-8">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">Dashboard</h1>
            <p className="mt-0.5 text-xs text-slate-300">Traffic · conversion · geography · content & registrations</p>
          </div>
          <div className="inline-flex rounded-xl bg-white/10 p-1 backdrop-blur" data-testid="analytics-range">
            {RANGES.map((r) => (
              <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setDays(r.key)}
                className={`rounded-lg px-3.5 py-1.5 text-xs font-bold transition-all ${days === r.key ? 'bg-[#FF5757] text-white shadow' : 'text-white/70 hover:text-white'}`}>
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={doExport} disabled={exporting} data-testid="analytics-export" className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-3.5 py-2 text-xs font-bold text-white backdrop-blur transition-all hover:bg-white/20 disabled:opacity-50">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="analytics-kpis">
          <StatCard icon={Eye} label="Page views" value={t.page_views.toLocaleString()} gradient="bg-gradient-to-br from-blue-500 to-indigo-600" />
          <StatCard icon={Users} label="Unique visitors" value={t.unique_visitors.toLocaleString()} gradient="bg-gradient-to-br from-violet-500 to-purple-600" />
          <StatCard icon={Globe} label="Unique IPs" value={t.unique_ips.toLocaleString()} gradient="bg-gradient-to-br from-cyan-500 to-sky-600" />
          <StatCard icon={TrendingUp} label="Enquiries" value={t.enquiries.toLocaleString()} sub={`${conv.visitor_to_lead}% of visitors`} gradient="bg-gradient-to-br from-amber-500 to-orange-600" />
          <StatCard icon={CreditCard} label="Registrations" value={reg.total.toLocaleString()} sub={`${reg.pre_to_post}% pre→post`} gradient="bg-gradient-to-br from-emerald-500 to-teal-600" />
          <StatCard icon={DollarSign} label="MRR" value={`$${reg.mrr_total.toLocaleString()}`} gradient="bg-gradient-to-br from-rose-500 to-pink-600" />
        </div>

        {/* Trend + Funnel */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title={`Traffic · last ${days} days`} icon={TrendingUp} className="lg:col-span-2">
            <div style={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <AreaChart data={data.trend} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0035A8" stopOpacity={0.35} /><stop offset="100%" stopColor="#0035A8" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f97316" stopOpacity={0.3} /><stop offset="100%" stopColor="#f97316" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" name="Page views" dataKey="views" stroke="#0035A8" strokeWidth={2.5} fill="url(#gv)" />
                  <Area type="monotone" name="Visitors" dataKey="visitors" stroke="#f97316" strokeWidth={2.5} fill="url(#gu)" />
                  {campaigns.map((c) => {
                    const lastDate = data.trend.length ? data.trend[data.trend.length - 1].date : c.start_date;
                    return (
                      <ReferenceArea key={c.id} x1={c.start_date} x2={c.end_date || lastDate} y1={0}
                        fill={CH_COLOR[c.channel] || '#64748b'} fillOpacity={0.1}
                        stroke={CH_COLOR[c.channel] || '#64748b'} strokeOpacity={0.4} strokeDasharray="3 3"
                        label={{ value: c.name, position: 'insideTop', fontSize: 9, fill: CH_COLOR[c.channel] || '#64748b' }} />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Conversion funnel" icon={TrendingUp}>
            <Funnel stages={data.funnel} />
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              <div className="rounded-xl bg-blue-50 p-2.5 text-center">
                <div className="text-lg font-extrabold text-[#0035A8]">{conv.visitor_to_lead}%</div>
                <div className="text-[10px] font-medium text-slate-500">Visitor → enquiry</div>
              </div>
              <div className="rounded-xl bg-emerald-50 p-2.5 text-center">
                <div className="text-lg font-extrabold text-emerald-600">{conv.lead_to_won}%</div>
                <div className="text-[10px] font-medium text-slate-500">Enquiry → won</div>
              </div>
            </div>
          </Panel>
        </div>

        {/* Campaigns */}
        <CampaignsCard campaigns={campaigns} onChange={loadCampaigns} />

        {/* Needs attention + Astrid alerts */}
        {engagement && <NeedsAttentionCard data={engagement} />}
        <AlertSettingsCard />

        {/* Paying funnel + MRR / churn */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="Trial → Registered → Paying" icon={TrendingUp}>
            <Funnel stages={payFunnel} />
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
              <div className="rounded-xl bg-blue-50 p-2.5 text-center"><div className="text-lg font-extrabold text-[#0035A8]">{rev.trial_to_reg ?? 0}%</div><div className="text-[10px] font-medium text-slate-500">Trial → Registered</div></div>
              <div className="rounded-xl bg-emerald-50 p-2.5 text-center"><div className="text-lg font-extrabold text-emerald-600">{rev.reg_to_paying ?? 0}%</div><div className="text-[10px] font-medium text-slate-500">Registered → Paying</div></div>
            </div>
          </Panel>

          <Panel title="Revenue · MRR & churn" icon={DollarSign} className="lg:col-span-2">
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="analytics-revenue-kpis">
              <div className="rounded-xl bg-gradient-to-br from-[#0035A8]/5 to-transparent p-3"><div className="text-[11px] font-medium text-slate-500">Total MRR</div><div className="mt-0.5 text-xl font-extrabold text-[#0035A8]">${(rev.mrr_total || 0).toLocaleString()}</div></div>
              <div className="rounded-xl bg-emerald-50 p-3"><div className="text-[11px] font-medium text-slate-500">New paying · {days}d</div><div className="mt-0.5 text-xl font-extrabold text-emerald-600">{rev.new_paying || 0}</div></div>
              <div className="rounded-xl bg-rose-50 p-3"><div className="text-[11px] font-medium text-slate-500">Churned · {days}d</div><div className="mt-0.5 text-xl font-extrabold text-rose-600">{rev.churned || 0}</div></div>
              <div className="rounded-xl bg-slate-50 p-3"><div className="text-[11px] font-medium text-slate-500">Churn rate</div><div className="mt-0.5 text-xl font-extrabold text-slate-800">{rev.churn_rate ?? 0}%</div></div>
            </div>
            <div style={{ width: '100%', height: 190 }} data-testid="analytics-mrr-trend">
              <ResponsiveContainer>
                <AreaChart data={rev.trend || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gnew" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gchurn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} /><stop offset="100%" stopColor="#f43f5e" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(d) => (d || '').slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" name="New paying" dataKey="new" stroke="#10b981" strokeWidth={2.5} fill="url(#gnew)" />
                  <Area type="monotone" name="Churned" dataKey="churned" stroke="#f43f5e" strokeWidth={2.5} fill="url(#gchurn)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Registrations insights */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="Registrations · pre vs post" icon={CreditCard}>
            <div style={{ width: '100%', height: 190 }} data-testid="analytics-registrations">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={regData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {regData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-1 rounded-xl bg-slate-50 p-2.5 text-center">
              <span className="text-xs text-slate-500">Pre→post conversion </span>
              <span className="text-sm font-extrabold text-[#0035A8]">{reg.pre_to_post}%</span>
            </div>
          </Panel>

          <Panel title="Registrations by plan" icon={Trophy}>
            <BarList items={reg.by_plan.map((p) => ({ label: p.plan, count: p.count }))} labelKey="label" valueKey="count" testid="analytics-plans" accent="#059669" />
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
              <span className="text-slate-500">New in last {days}d</span>
              <span className="font-bold text-slate-900">{reg.new_in_window}</span>
            </div>
          </Panel>

          <Panel title="Newsletter engagement" icon={Mail}>
            <div className="flex items-center justify-around" data-testid="analytics-newsletter">
              <Gauge value={nl.open_rate} label="Open rate" color="#0035A8" />
              <Gauge value={nl.click_rate} label="Click rate" color="#f59e0b" />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
              <div><div className="text-sm font-extrabold text-slate-900">{data.content.newsletters_sent}</div><div className="text-[10px] text-slate-500">Sent</div></div>
              <div><div className="text-sm font-extrabold text-slate-900">{nl.sends}</div><div className="text-[10px] text-slate-500">Emails</div></div>
              <div><div className="text-sm font-extrabold text-slate-900">{nl.opens}</div><div className="text-[10px] text-slate-500">Opens</div></div>
            </div>
          </Panel>
        </div>

        {/* Content performance */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="Top content by views" icon={FileText} className="lg:col-span-2"
            right={<span className="text-[11px] text-slate-400">{data.content.blogs_published} blogs · {data.content.case_studies_published} case studies</span>}>
            <div className="space-y-2.5" data-testid="analytics-top-content">
              {data.content.top_content.map((c, i) => {
                const max = Math.max(1, ...data.content.top_content.map((x) => x.views));
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold ${c.type === 'Blog' ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>{c.type === 'Blog' ? <Newspaper className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-slate-700" title={c.title}>{c.title}</div>
                      <div className="h-1.5 mt-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0035A8]" style={{ width: `${(c.views / max) * 100}%` }} /></div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-bold text-slate-900">{c.views}<span className="ml-0.5 text-[10px] font-normal text-slate-400">views</span></div>
                      <div className="flex items-center justify-end gap-0.5 text-[10px] font-semibold text-emerald-600"><MousePointerClick className="h-3 w-3" />{c.engagement}</div>
                    </div>
                  </div>
                );
              })}
              {data.content.top_content.length === 0 && <p className="text-sm text-slate-400">No published content yet.</p>}
            </div>
          </Panel>

          <Panel title="Devices" icon={Smartphone}>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={deviceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {deviceData.map((d, i) => <Cell key={i} fill={DEVICE_COLORS[d.name] || '#94a3b8'} />)}
                  </Pie>
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Geography */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="Top countries" icon={Globe}>
            <BarList items={data.by_country} labelKey="country" valueKey="views" testid="analytics-countries" accent="#0035A8" prefixFlag />
          </Panel>
          <Panel title="States / regions" icon={MapPin}>
            <BarList items={data.by_region} labelKey="region" valueKey="views" testid="analytics-regions" accent="#7c3aed" prefixFlag />
          </Panel>
          <Panel title="Traffic sources" icon={Repeat}>
            <BarList items={data.by_source} labelKey="source" valueKey="visitors" testid="analytics-sources" accent="#0891b2" />
          </Panel>
        </div>

        {/* Top pages + Outreach */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="Top 10 pages" icon={Eye} className="lg:col-span-2">
            <BarList items={data.top_pages} labelKey="path" valueKey="views" testid="analytics-top-pages" accent="#0035A8" />
          </Panel>
          <Panel title="Outreach & LinkedIn" icon={Send}>
            <div className="space-y-3 text-sm" data-testid="analytics-outreach">
              {[
                { label: 'Booking links sent', value: data.outreach.booking_links_sent, icon: Send },
                { label: 'Retargeting drafts', value: data.outreach.retarget_drafts, icon: Repeat },
                { label: 'Retargeting sent', value: data.outreach.retarget_sent, icon: Send },
                { label: 'Newsletters sent', value: data.outreach.newsletters_sent, icon: Mail },
                { label: 'LinkedIn posts', value: data.linkedin.total_posts, icon: Linkedin },
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                  <span className="flex items-center gap-2 text-slate-600"><r.icon className="h-3.5 w-3.5 text-slate-400" /> {r.label}</span>
                  <span className="font-bold text-slate-900">{r.value}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* Weekly report settings */}
        <ReportCard />

        {/* Recent visitors / IP tracking */}
        <Panel title="Recent visitors · IP tracking" icon={Globe} className="mt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs" data-testid="analytics-visitors-table">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-3 font-semibold">IP address</th>
                  <th className="py-2 pr-3 font-semibold">Location</th>
                  <th className="py-2 pr-3 font-semibold">Org / ISP</th>
                  <th className="py-2 pr-3 font-semibold">Last page</th>
                  <th className="py-2 pr-3 font-semibold">Source</th>
                  <th className="py-2 pr-3 font-semibold">Device</th>
                  <th className="py-2 pr-3 text-right font-semibold">Views</th>
                  <th className="py-2 pr-3 font-semibold">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => (
                  <tr key={v.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-mono text-slate-700">{v.ip || '—'}</td>
                    <td className="py-2 pr-3 text-slate-600"><span className="mr-1">{flag(v.country_code)}</span>{[v.city, v.country].filter(Boolean).join(', ') || 'Unknown'}</td>
                    <td className="py-2 pr-3 max-w-[180px] truncate text-slate-500" title={v.org}>{v.org || '—'}</td>
                    <td className="py-2 pr-3 max-w-[160px] truncate text-slate-600" title={v.last_path}>{v.last_path}</td>
                    <td className="py-2 pr-3 text-slate-600">{v.source}</td>
                    <td className="py-2 pr-3 capitalize text-slate-600">{v.device}</td>
                    <td className="py-2 pr-3 text-right font-bold text-slate-900">{v.views}</td>
                    <td className="py-2 pr-3 text-slate-500">{v.last_seen ? new Date(v.last_seen).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {visitors.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">No visitor data yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
